import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

function StatusBadge({ status }) {
  const cls = status === "published"
    ? "bg-[var(--rciq-teal)]/15 text-[var(--rciq-teal-dark)]"
    : "bg-amber-100 text-amber-700";
  return <span className={`text-[10px] uppercase tracking-[0.1em] font-700 px-2.5 py-1 rounded-full ${cls}`}>{status}</span>;
}

export default function AdminDashboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    api.get("/articles/admin/all")
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this article? This cannot be undone.")) return;
    await api.delete(`/articles/${id}`);
    load();
  };

  const filtered = items.filter((a) => filter === "all" ? true : a.status === filter);
  const stats = {
    total: items.length,
    published: items.filter((a) => a.status === "published").length,
    drafts: items.filter((a) => a.status === "draft").length,
    views: items.reduce((sum, a) => sum + (a.views || 0), 0),
  };

  return (
    <div data-testid="admin-dashboard" className="space-y-8">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { k: "Total", v: stats.total, tid: "stat-total" },
          { k: "Published", v: stats.published, tid: "stat-published" },
          { k: "Drafts", v: stats.drafts, tid: "stat-drafts" },
          { k: "Total Views", v: stats.views, tid: "stat-views" },
        ].map((s) => (
          <div key={s.k} className="bg-white border border-[var(--rciq-line)] rounded-2xl p-5" data-testid={s.tid}>
            <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--rciq-muted)] font-700">{s.k}</div>
            <div className="font-serif-display text-3xl font-700 mt-1.5">{s.v}</div>
          </div>
        ))}
      </div>

      {/* Filter + new */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2">
          {["all", "published", "draft"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
              className={`text-[11px] uppercase tracking-[0.12em] font-700 px-3.5 py-2 rounded-full transition ${
                filter === f ? "bg-[var(--rciq-ink)] text-white" : "bg-white border border-[var(--rciq-line)] text-[var(--rciq-muted)] hover:text-[var(--rciq-ink)]"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <Link to="/admin/new" className="btn-pill btn-pill-primary text-[12px]" data-testid="new-article-btn">
          + Write New Article
        </Link>
      </div>

      {/* List */}
      <div className="bg-white border border-[var(--rciq-line)] rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[var(--rciq-muted)]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center" data-testid="admin-empty">
            <p className="text-[var(--rciq-muted)]">No articles yet.</p>
            <Link to="/admin/new" className="mt-4 inline-block btn-pill btn-pill-primary text-[12px]">
              Write your first article
            </Link>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[var(--rciq-bg-tint)] text-[11px] uppercase tracking-[0.1em] text-[var(--rciq-muted)] font-700">
              <tr>
                <th className="text-left p-4">Title</th>
                <th className="text-left p-4 hidden md:table-cell">Category</th>
                <th className="text-left p-4 hidden md:table-cell">Status</th>
                <th className="text-left p-4 hidden lg:table-cell">Updated</th>
                <th className="text-left p-4 hidden lg:table-cell">Views</th>
                <th className="p-4 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.id} className="border-t border-[var(--rciq-line)] hover:bg-[var(--rciq-bg-tint)]/60" data-testid={`row-${a.slug}`}>
                  <td className="p-4">
                    <Link to={`/admin/edit/${a.id}`} className="font-serif-display text-[1.05rem] font-600 text-[var(--rciq-ink)] hover:text-[var(--rciq-teal-dark)]">
                      {a.title}
                    </Link>
                    {a.featured && <span className="ml-2 text-[10px] uppercase tracking-[0.1em] text-[var(--rciq-teal-dark)] font-700">★ Featured</span>}
                  </td>
                  <td className="p-4 hidden md:table-cell text-[var(--rciq-muted)]">{a.category}</td>
                  <td className="p-4 hidden md:table-cell"><StatusBadge status={a.status} /></td>
                  <td className="p-4 hidden lg:table-cell text-[var(--rciq-muted)] text-xs">
                    {new Date(a.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </td>
                  <td className="p-4 hidden lg:table-cell text-[var(--rciq-muted)]">{a.views || 0}</td>
                  <td className="p-4 text-right">
                    <Link to={`/admin/edit/${a.id}`} className="text-xs font-700 text-[var(--rciq-teal-dark)] hover:underline" data-testid={`edit-${a.slug}`}>Edit</Link>
                    <button onClick={() => remove(a.id)} className="ml-4 text-xs font-700 text-red-500 hover:underline" data-testid={`delete-${a.slug}`}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
